/**
 * Утилиты для нормализации и сопоставления имён
 */

// Префиксы и суффиксы, которые нужно удалить из имён
const NAME_PREFIXES = [
  '[Personal Holidays]',
  'Personal Holidays',
  '[Sick Leave]',
  'Sick Leave',
  '[Travel Day]',
  'Travel Day',
  'vacation',
  'Sick leave',
  'sick leave',
  'Day off',
  'day off',
  'Day Off',
];

// Суффиксы, которые нужно удалить из имён (в конце строки)
const NAME_SUFFIXES = ['lieu days', 'lieu', 'days'];

// Варианты написания имён для сопоставления
const NAME_VARIANTS: Record<string, string[]> = {
  // Tatiana / Tatyana
  Tatiana: ['Tatyana', 'Tatiana', 'Tatyanna'],
  Tatyana: ['Tatiana', 'Tatyana', 'Tatyanna'],

  // Alexander / Aleksandr / Aleksander / Alexandr
  Alexander: ['Aleksandr', 'Aleksander', 'Alexandr', 'Alexander', 'Alex'],
  Aleksandr: ['Alexander', 'Aleksander', 'Alexandr', 'Aleksandr', 'Alex'],
  Aleksander: ['Alexander', 'Aleksandr', 'Alexandr', 'Aleksander', 'Alex'],
  Alexandr: ['Alexander', 'Aleksandr', 'Aleksander', 'Alexandr', 'Alex'],

  // Другие распространённые варианты
  Evgeny: ['Evgeniy', 'Evgeny', 'Eugene'],
  Evgeniy: ['Evgeny', 'Evgeniy', 'Eugene'],
  Kirill: ['Kirill', 'Kiril', 'Cyril'],
  Gennadii: ['Gennady', 'Gennadii', 'Gennadi'],
  Gennady: ['Gennadii', 'Gennady', 'Gennadi'],
  Nikolay: ['Nikolai', 'Nikolay', 'Nicholas'],
  Nikolai: ['Nikolay', 'Nikolai', 'Nicholas'],
};

/**
 * Нормализует имя: убирает префиксы, суффиксы и лишние слова
 */
export function normalizeName(name: string): string {
  if (!name) return '';

  let normalized = name.trim();

  // Убираем префиксы (с квадратными скобками и без)
  for (const prefix of NAME_PREFIXES) {
    // Вариант с квадратными скобками
    const withBrackets = `[${prefix}]`;
    if (normalized.startsWith(withBrackets)) {
      normalized = normalized.substring(withBrackets.length).trim();
    }
    // Вариант без квадратных скобок
    if (normalized.startsWith(prefix)) {
      normalized = normalized.substring(prefix.length).trim();
    }
    // Также проверяем, если префикс в середине или конце (с любыми скобками)
    const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\[?${escapedPrefix}\\]?`, 'gi');
    normalized = normalized.replace(regex, '').trim();
  }

  // Убираем суффиксы (в конце строки)
  for (const suffix of NAME_SUFFIXES) {
    // Проверяем суффикс в конце (с пробелом перед ним)
    const suffixWithSpace = ` ${suffix}`;
    if (normalized.toLowerCase().endsWith(suffixWithSpace.toLowerCase())) {
      normalized = normalized.substring(0, normalized.length - suffixWithSpace.length).trim();
    }
    // Также проверяем, если суффикс стоит отдельно в конце
    const escapedSuffix = suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const suffixRegex = new RegExp(`\\s+${escapedSuffix}\\s*$`, 'gi');
    normalized = normalized.replace(suffixRegex, '').trim();
  }

  // Убираем лишние пробелы
  normalized = normalized.replace(/\s+/g, ' ').trim();

  return normalized;
}

/**
 * Сопоставляет варианты написания имени и возвращает каноническую версию
 */
export function canonicalizeName(name: string): string {
  if (!name) return '';

  const normalized = normalizeName(name);
  const parts = normalized.split(' ');

  if (parts.length === 0) return normalized;

  // Проверяем первый элемент (имя) на варианты написания
  const firstName = parts[0];
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

  // Ищем каноническую версию имени
  for (const [canonical, variants] of Object.entries(NAME_VARIANTS)) {
    if (variants.some((v) => v.toLowerCase() === firstName.toLowerCase())) {
      return lastName ? `${canonical} ${lastName}` : canonical;
    }
  }

  // Если не нашли вариант, возвращаем нормализованное имя с заглавной первой буквой
  const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  return lastName ? `${capitalizedFirstName} ${lastName}` : capitalizedFirstName;
}

/**
 * Проверяет, являются ли два имени вариантами написания одного человека
 */
export function areNamesMatching(name1: string, name2: string): boolean {
  const canonical1 = canonicalizeName(name1);
  const canonical2 = canonicalizeName(name2);

  // Сравниваем канонические версии
  if (canonical1.toLowerCase() === canonical2.toLowerCase()) {
    return true;
  }

  // Также проверяем, если имена совпадают после нормализации
  const norm1 = normalizeName(name1).toLowerCase();
  const norm2 = normalizeName(name2).toLowerCase();

  return norm1 === norm2;
}

/**
 * Группирует имена по каноническим версиям
 */
export function groupNamesByCanonical(names: string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();

  for (const name of names) {
    const canonical = canonicalizeName(name);
    if (!groups.has(canonical)) {
      groups.set(canonical, []);
    }
    groups.get(canonical)!.push(name);
  }

  return groups;
}

/**
 * Получает список уникальных канонических имён из массива
 */
export function getUniqueCanonicalNames(names: string[]): string[] {
  const canonicalSet = new Set<string>();

  for (const name of names) {
    const canonical = canonicalizeName(name);
    canonicalSet.add(canonical);
  }

  return Array.from(canonicalSet).sort((a, b) => a.localeCompare(b));
}

/** Word = 2+ letters (Latin + Cyrillic). Matches legacy SharePoint title parsing. */
const TITLE_WORD_REGEX = /[a-zA-Z\u0400-\u04FF]{2,}/g;

const TITLE_PREFIXES = [
  'SP:',
  'SP ',
  '[Personal Holidays]',
  'Personal Holidays',
  '[Sick Leave]',
  'Sick Leave',
  '[Travel Day]',
  'Travel Day',
];

/**
 * Extract "Firstname Lastname" from a SharePoint-style calendar title (e.g. "SP Name - vacation").
 */
export function extractPersonNameFromCalendarTitle(
  title: string | undefined | null
): string | null {
  if (!title || typeof title !== 'string') return null;

  let s = title.trim();
  for (const prefix of TITLE_PREFIXES) {
    if (s.startsWith(prefix)) {
      s = s.slice(prefix.length).trim();
      break;
    }
  }
  s = s.replace(/[-–—.,;:]/g, ' ');
  const words = (s.match(TITLE_WORD_REGEX) || []).filter((w) => w.length >= 2);
  if (words.length > 0 && words[0].toLowerCase() === 'sp') words.shift();
  if (words.length < 2) return null;
  return words.slice(0, 2).join(' ');
}
